import type * as CoreBundle from '@foundation-player/core';
import {
  FoundationErrorEvent,
  MetricEvent,
  PlayerEvent,
  PlayerMetric,
  RequestType,
  getVersion,
  type PlayerInterface,
} from '@foundation-player/core';
import { PlayerError } from '@foundation-player/error-codes';
import { createLogger } from '@foundation-player/logger';
import { createUAParser } from '@foundation-player/platform-check';
import type * as PlayerBitmovinBundle from '@foundation-player/player-bitmovin';
import { ChromecastPlayerContainer } from '@foundation-player/player-container-chromecast';
import { DomPlayerContainer } from '@foundation-player/player-container-dom';
import type * as PlayerDashJsBundle from '@foundation-player/player-dashjs';
import type * as PlayerNativeBundle from '@foundation-player/player-native';
import { ConsentCallback, RequestHeader, RequestHeaderCallback } from '@foundation-player/types';
import { BuilderConfig, PlayerType, PluginFactory, PluginFunction, UserConfig } from './builder-config';
import { transformUserToBuilderConfig } from './configuration/transform-user-to-builder-config';
import { FoundationGlobal } from './foundation-global';
import { loadPackage } from './load-package';
import { PackageName } from './package-name';
import { createAdvertisingPlugin } from './plugin/advertising';
import { createBitmovinAnalyticsPlugin } from './plugin/bitmovin-analytics';
import { createConcurrentStreamCheckPlugin } from './plugin/concurrent-stream-check';
import { createDeviceHeaderPlugin } from './plugin/device-header';
import { createEntitlementPlugin } from './plugin/entitlement';
import { createGoogleAnalytics4Plugin } from './plugin/google-analytics-4';
import { createHeartbeatPlugin } from './plugin/heartbeat';
import { createMediaSessionPlugin } from './plugin/media-session';
import { createNielsenPlugin } from './plugin/nielsen';
import { createNpawAnalyticsPlugin } from './plugin/npaw-analytics';
import { createOnScreenLogPlugin } from './plugin/on-screen-log';
import { createRcuBindingsPlugin } from './plugin/rcu-bindings';
import { createSsaiPlugin } from './plugin/ssai';
import { createStreamFallbackPlugin } from './plugin/stream-fallback';
import { createVisibilityPlugin } from './plugin/visibility';
import { resolveByPackageName } from './resolve-bundle';

type PlayerBundle = typeof PlayerBitmovinBundle | typeof PlayerDashJsBundle | typeof PlayerNativeBundle;

const PLUGIN_FACTORY: PluginFactory = {
  'foundation.plugin.advertising': createAdvertisingPlugin,
  'foundation.plugin.bitmovinAnalytics': createBitmovinAnalyticsPlugin,
  'foundation.plugin.concurrentStreamCheck': createConcurrentStreamCheckPlugin,
  'foundation.plugin.deviceHeader': createDeviceHeaderPlugin,
  'foundation.plugin.entitlement': createEntitlementPlugin,
  'foundation.plugin.googleAnalytics4': createGoogleAnalytics4Plugin,
  'foundation.plugin.heartbeat': createHeartbeatPlugin,
  'foundation.plugin.mediaSession': createMediaSessionPlugin,
  'foundation.plugin.nielsen': createNielsenPlugin,
  'foundation.plugin.npawAnalytics': createNpawAnalyticsPlugin,
  'foundation.plugin.onScreenLog': createOnScreenLogPlugin,
  'foundation.plugin.rcuBindings': createRcuBindingsPlugin,
  'foundation.plugin.ssai': createSsaiPlugin,
  'foundation.plugin.streamFallback': createStreamFallbackPlugin,
  'foundation.plugin.visibility': createVisibilityPlugin,
};

export class Builder {
  private static getConsentStringCallback(consentString: string): ConsentCallback {
    return () => Promise.resolve(consentString);
  }

  private static getRequestHeaderCallback(authToken: string): RequestHeaderCallback {
    const requestTypes = new Map<RequestType, RequestHeader>();

    // These are the request types known to need the x-auth-token header
    const xAuthTokenRequestTypes: RequestType[] = [RequestType.DRM, RequestType.Entitlement, RequestType.Nielsen];
    for (const requestType of xAuthTokenRequestTypes) {
      requestTypes.set(requestType, { 'x-auth-token': authToken });
    }

    // These are the request types known to need the same value but with the jwt key
    requestTypes.set(RequestType.ConcurrentStreamCheck, { jwt: authToken });

    return (requestType: RequestType) => requestTypes.get(requestType) || ({} as RequestHeader);
  }

  constructor(private readonly pluginFactory: PluginFactory = PLUGIN_FACTORY) {}

  /**
   * Load all needed bundles according to the specified builderConfig and create the player.
   * @param userConfig options passed to createPlayer of the specified root player
   * @returns instantiated player
   */
  async build(userConfig: UserConfig): Promise<PlayerInterface> {
    const builderStart = performance.now();
    const version = getVersion();

    await loadPackage({
      packageName: PackageName.Logger,
      bundleType: userConfig.bundleType,
      version,
      isLocal: userConfig.isLocal,
      cdn: userConfig.cdn,
    });

    const foundationGlobal = (window as unknown as FoundationGlobal).foundation;
    createLogger(foundationGlobal, userConfig.playerConfig?.loggerConfig);
    createUAParser(foundationGlobal);

    const builderConfig = await transformUserToBuilderConfig(userConfig);
    const { playerPackage, packages, pluginFactories } = this.executeFactories(builderConfig);

    const promises = packages.map((packageName) => {
      return loadPackage({
        packageName,
        bundleType: builderConfig.bundleType,
        version,
        isLocal: builderConfig.isLocal,
        cdn: builderConfig.cdn,
      });
    });
    await Promise.all(promises);

    const plugins = pluginFactories.flatMap((pluginFactory) => pluginFactory());

    const { playerElementOrId, videoElementOrId } = userConfig;
    const playerConfig = builderConfig.playerConfig;

    const coreBundle = resolveByPackageName<typeof CoreBundle>(PackageName.Core);
    if (!coreBundle) {
      throw new FoundationErrorEvent(PlayerEvent.Error, {
        message: 'Core bundle could not be found!',
        code: PlayerError.PLAYER_DEPENDENCIES_COULD_NOT_BE_LOADED_52110.code,
      });
    }

    const playerBundle = resolveByPackageName<PlayerBundle>(playerPackage);
    if (!playerBundle) {
      throw new FoundationErrorEvent(PlayerEvent.Error, {
        message: `Player bundle "${playerPackage}" could not be found!`,
        code: PlayerError.PLAYER_DEPENDENCIES_COULD_NOT_BE_LOADED_52110.code,
      });
    }

    // TODO: In the kepler MR we will add a KeplerPlayerContainer and make the
    // builder choose the right one depending on the current environment
    let playerContainer;

    if (builderConfig.playerType === PlayerType.Chromecast) {
      playerContainer = new ChromecastPlayerContainer(playerElementOrId);
    } else {
      playerContainer = new DomPlayerContainer(playerElementOrId, videoElementOrId);
    }

    for (const plugin of plugins) {
      if (plugin.beforeDispatch) {
        playerContainer.addDispatchGate(plugin.beforeDispatch.bind(plugin));
      }
    }

    const player = await coreBundle.createPlayer(
      playerBundle,
      {
        playerConfig,
        playerPlugins: plugins,
      },
      userConfig,
      builderConfig.remoteConfig,
      playerContainer,
    );

    // Shortcut for when the consent string is known at creation:
    // Set a consent callback with its value
    if (userConfig.consentString) {
      player.setConsentCallback(Builder.getConsentStringCallback(userConfig.consentString));
    }

    // Shortcut for when the auth-token is known at creation:
    // Set a request-header callback with its value
    if (userConfig.authToken) {
      player.setRequestHeaderCallback(Builder.getRequestHeaderCallback(userConfig.authToken));
    }

    const builderEnd = performance.now();
    player.dispatchEvent(new MetricEvent(PlayerMetric.BuilderCreation, { start: builderStart, end: builderEnd }));
    return player;
  }

  /**
   * Used for build() and externally to create platform bundles
   */
  executeFactories(builderConfig: BuilderConfig): {
    playerPackage: PackageName;
    packages: PackageName[];
    pluginFactories: PluginFunction[];
  } {
    const playerPackage = this.getPlayerPackage(builderConfig);
    // we use a set here to make sure every dependency is loaded just once
    // for example if we have multiple plugins which use our platform-check
    // the platform-check is just loaded once
    const packages = new Set<PackageName>([PackageName.Core, playerPackage]);
    const pluginFactories: PluginFunction[] = [];

    const factories = Object.entries(this.pluginFactory) as [keyof PluginFactory, PluginFactory[keyof PluginFactory]][];

    factories
      .filter(([globalName]) => builderConfig.pluginConfigs[globalName]?.enabled)
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .forEach(([_, factory]) => {
        const result = factory(builderConfig);

        if (result) {
          const [pluginPackages, pluginFactory] = result;
          pluginPackages.forEach((pluginPackage) => packages.add(pluginPackage));
          pluginFactories.push(pluginFactory);
        }
      });

    return {
      playerPackage,
      packages: Array.from(packages.values()),
      pluginFactories,
    };
  }

  private getPlayerPackage(builderConfig: BuilderConfig): PackageName {
    switch (builderConfig.playerType) {
      case PlayerType.Bitmovin:
        return PackageName.PlayerBitmovin;
      case PlayerType.DashJs:
        return PackageName.PlayerDashjs;
      case PlayerType.Native:
        return PackageName.PlayerNative;
      case PlayerType.Chromecast:
        return PackageName.PlayerChromecast;
      default:
        throw new FoundationErrorEvent(PlayerEvent.Error, {
          message: 'Unknown playerType "${builderConfig.playerType}" given!',
          code: PlayerError.INTERNAL_PLAYER_ERROR_50000.code,
        });
    }
  }
}
